import * as React from 'react';
import {observer} from 'mobx-react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import UserStore from 'stores/UserStore';
import SidebarContext from '../../SidebarContext';
import Avatar from './components/Avatar';
import ControlButton from './components/ControlButton';
import Dropdown from 'components/Dropdown';
import {IconMore, IconMessage} from 'icons';

const Container = styled.div`
  margin-bottom: 16px;
  gap: 14px;
  justify-content: center;
  display: grid;
`;

const Controls = styled.div<{
  isToggle?: boolean;
}>`
  gap: 12px;
  align-items: center;
  grid-auto-flow: ${({isToggle}) => isToggle ? 'column' : 'row'};
  display: inline-grid;
`;

const Content = styled.div`
  text-align: center;
  display: grid;
  gap: 4px;
`;

const Username = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const Role = styled.span`
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
`;

/**
 * This component renders the sidebar header.
 * @return {React.ReactElement}
 * @constructor
 */
function Header(): React.ReactElement {
  const context = React.useContext(SidebarContext);

  /**
   * Render controls.
   * @return {JSX.Element}
   */
  function renderControls(): JSX.Element {
    return (
      <Controls isToggle={context?.isToggle}>
        <Dropdown gap={6} trigger={(open) => (
          <ControlButton onClick={() => open()}>
            <IconMore />
          </ControlButton>
        )}>
          <Dropdown.Item as="a" href="/settings">
            Профиль
          </Dropdown.Item>
          <Dropdown.Item as="a" href="/tariffs">
            Тарифы
          </Dropdown.Item>
          <Dropdown.Item as="a" href="/logout">
            Выйти из аккаунта
          </Dropdown.Item>
        </Dropdown>
        <Avatar />
        <ControlButton>
          <IconMessage />
        </ControlButton>
      </Controls>
    );
  }

  /**
   * Render user content.
   * @return {JSX.Element | undefined}
   */
  function renderContent(): JSX.Element | undefined {
    /**
     * Render username.
     * @return {JSX.Element}
     */
    function renderUserName(): JSX.Element {
      return (
        <Username>
          {UserStore.state?.username}
        </Username>
      );
    }

    /**
     * Render user role.
     * @return {JSX.Element}
     */
    function renderUserRole(): JSX.Element {
      return (
        <Role>
          {UserStore.getRoleLabel()}
        </Role>
      );
    }

    return context?.isToggle ? (
      <Content>
        {renderUserName()}
        {renderUserRole()}
      </Content>
    ) : undefined;
  }

  return (
    <Container>
      {renderControls()}
      {renderContent()}
    </Container>
  );
}

export default observer(Header);
